// react
import { forwardRef } from 'react';

// material ui icons
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

// material ui
import { alpha, CircularProgress } from '@mui/material';
import Button, { type ButtonProps } from '@mui/material/Button';

export interface DeleteButtonProps extends Omit<
  ButtonProps,
  'variant' | 'color'
> {
  /** Shows a spinner in place of `startIcon` and disables the button. */
  loading?: boolean;
}

/**
 * Reusable destructive "delete" action button.
 *
 * The general button look (padding, border-radius, typography, etc.) comes
 * from the theme's `MuiButton` styleOverrides. This component only owns the
 * delete-specific color palette, hover/disabled states and default icon.
 */
export const DeleteButton = forwardRef<HTMLButtonElement, DeleteButtonProps>(
  (
    {
      loading = false,
      disabled,
      startIcon = <DeleteOutlineRoundedIcon />,
      sx,
      ...rest
    },
    ref,
  ) => (
    <Button
      ref={ref}
      variant="contained"
      disabled={disabled || loading}
      startIcon={
        loading ? <CircularProgress size={16} color="inherit" /> : startIcon
      }
      sx={[
        (theme) => ({
          color: '#ffffff',
          border:
            theme.palette.mode === 'dark'
              ? '1px solid #f85149'
              : '1px solid #1b1f2326',
          backgroundColor:
            theme.palette.mode === 'dark' ? '#da3633' : '#cf222e',
          borderColor: theme.palette.mode === 'dark' ? '#f85149' : '#1b1f2326',
          boxShadow:
            theme.palette.mode === 'dark'
              ? 'rgba(1, 4, 9, 0.35) 0px 1px 0px 0px, rgba(255, 255, 255, 0.06) 0px 1px 0px 0px inset'
              : 'rgba(27, 31, 35, 0.04) 0px 1px 0px 0px, rgba(255, 255, 255, 0.25) 0px 1px 0px 0px inset',
          transition: '0.2s cubic-bezier(0.3, 0, 0.5, 1)',
          transitionProperty: 'color, background-color, border-color',
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark' ? '#f85149' : '#a40e26',
            borderColor:
              theme.palette.mode === 'dark' ? '#ff7b72' : '#1b1f2326',
            transitionDuration: '0.1s',
            color: '#ffffff',
          },
          '&.Mui-disabled': {
            color: alpha('#ffffff', 0.7),
            borderColor:
              theme.palette.mode === 'dark'
                ? alpha('#f85149', 0.35)
                : alpha('#1b1f2326', 0.34),
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha('#da3633', 0.45)
                : alpha('#cf222e', 0.45),
            boxShadow: 'none',
          },
        }),
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
      {...rest}
    />
  ),
);

DeleteButton.displayName = 'DeleteButton';
